/* Drawer recommendations: shared controller survives cart redraws. */
(() => {
  if (window.__sjCartRecommendations) return;
  window.__sjCartRecommendations = true;
  /* --------------- helpers --------------- */
  const isVisible = (el) => {
    const r = el?.getBoundingClientRect?.();
    return !!r && r.width > 0 && r.height > 0;
  };
  function onReady(fn) {
    if (document.readyState === 'complete' || document.readyState === 'interactive') queueMicrotask(fn);
    else window.addEventListener('DOMContentLoaded', fn, { once: true });
  }
  /* --------------- init / refresh logic --------------- */
  function wireButtons(root) {
    // Prevent form submits
    root
      .querySelectorAll('.swiper-button-prev, .swiper-button-next, .cart-rec-card__add')
      .forEach((btn) => btn.setAttribute('type', 'button'));
  }

  function buildSwiper(container) {
    const root = container.closest('.cart-recommended');
    if (!root) return null;

    wireButtons(root);

    const nextEl = root.querySelector('.swiper-button-next');
    const prevEl = root.querySelector('.swiper-button-prev');

    const inst = new Swiper(container, {
      slidesPerView: 1,
      spaceBetween: 0,
      centeredSlides: false,
      watchOverflow: true,
      navigation: nextEl && prevEl ? { nextEl, prevEl } : undefined,
      preventClicks: true,
      preventClicksPropagation: true,
      threshold: 5,
      touchStartPreventDefault: true,
      breakpoints: {
    560: { slidesPerView: 1, spaceBetween: 0 },
    960: { slidesPerView: 1, spaceBetween: 0 },
      },
    });

    // Disable link clicks only while swiping
    let swiping = false;
    inst.on('touchStart', () => {
      swiping = true;
      container.classList.add('is-swiping');
    });
    inst.on('touchEnd', () => {
      setTimeout(() => {
    swiping = false;
    container.classList.remove('is-swiping');
      }, 0);
    });
    container.addEventListener(
      'click',
      (e) => {
    if (!swiping) return;
    const a = e.target.closest('a');
    if (a) {
      e.preventDefault();
      e.stopPropagation();
    }
      },
      true
    );

    return inst;
  }

  function refreshOrInitContainer(container) {
    if (!container || !isVisible(container)) return;

    // New container: init
    if (!container.classList.contains('swiper-initialized')) {
      buildSwiper(container);
      return;
    }

    // Existing instance: re-wire nav + update
    const inst = container.swiper;
    if (!inst) {
      // Defensive: container has class but no instance — rebuild
      container.classList.remove('swiper-initialized');
      buildSwiper(container);
      return;
    }

    const root = container.closest('.cart-recommended');
    const nextEl = root?.querySelector('.swiper-button-next') || null;
    const prevEl = root?.querySelector('.swiper-button-prev') || null;

    // If wrapper/slides were replaced, Swiper may be out-of-sync: rebuild
    const wrapper = container.querySelector('.swiper-wrapper');
    const slideCount = wrapper ? wrapper.querySelectorAll('.swiper-slide').length : 0;
    const needsRebuild = !wrapper || slideCount === 0;

    if (needsRebuild) {
      try {
    inst.destroy(true, true);
      } catch (e) {}
      container.classList.remove('swiper-initialized', 'swiper-horizontal', 'swiper-backface-hidden');
      buildSwiper(container);
      return;
    }

    // Re-bind navigation to fresh buttons
    if (inst.params.navigation) {
      try {
    inst.navigation.destroy();
      } catch (e) {}
      inst.params.navigation.nextEl = nextEl;
      inst.params.navigation.prevEl = prevEl;
      try {
    inst.navigation.init();
      } catch (e) {}
    }

    // Update sizes/slides
    try {
      inst.update();
    } catch (e) {}
  }

  function refreshAll() {
    const containers = document.querySelectorAll('.cart-recommended [data-swiper-container]');
    if (!containers.length) return;
    if (!window.Swiper) {
      if (window.SajadaSwiper) window.SajadaSwiper.ready(refreshAll);
      else document.addEventListener('sajada:swiper:loader-ready', refreshAll, { once: true });
      return;
    }
    containers.forEach(refreshOrInitContainer);
  }

  /* --------------- delegated Add-to-Cart (stable across re-renders) --------------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.cart-rec-card__add');
    if (!btn) return;

    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    try {
      const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
      });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
    document.dispatchEvent(
      new CustomEvent('theme-cart:updated', {
        detail: {
      error: (data && (data.description || data.message)) || 'Could not add to cart.',
      productTitle: (data && (data.product_title || data.title)) || '',
        },
      })
    );
    throw new Error((data && (data.description || data.message)) || 'Cart add failed');
      }

      btn.textContent = 'Added ✓';
      setTimeout(() => {
    btn.disabled = false;
    btn.textContent = original;
      }, 1200);

      document.dispatchEvent(
    new CustomEvent('theme-cart:updated', {
      detail: { shouldOpenCart: true, productTitle: (data && (data.product_title || data.title)) || '' },
    })
      );
    } catch (err) {
      console.error('[cart-recommended] add to cart error:', err);
      btn.disabled = false;
      btn.textContent = original;
    }
  });

  /* --------------- observe drawer DOM (covers remove/qty changes) --------------- */
  function startObserver() {
    // Watch the drawer host; fall back to <body> if needed
    const host =
      document.querySelector('section-quick-cart') ||
      document.querySelector('.quick-cart__container') ||
      document.querySelector('component-panel') ||
      document.body;

    const mo = new MutationObserver((mutations) => {
      // If drawer content changed, refresh sliders
      if (
    mutations.some(
      (m) =>
        m.type === 'childList' &&
        [...m.addedNodes].some(
      (n) =>
        n.nodeType === 1 &&
        (n.matches?.('.cart-recommended, [data-swiper-container], [data-update-items]') ||
          n.querySelector?.('.cart-recommended [data-swiper-container]'))
        )
    )
      ) {
    setTimeout(() => refreshAll(), 80);
      }
    });

    mo.observe(host, { childList: true, subtree: true });
  }

  /* --------------- boot --------------- */
  onReady(() => {
    refreshAll();
    startObserver();
  });

  // Extra theme hooks
  document.addEventListener('shopify:section:load', () => refreshAll());
  document.addEventListener('theme-quickCart:open', () => refreshAll());
  document.addEventListener('theme-cart:updated', () => {
    // let your theme finish updateContent(), then refresh
    setTimeout(() => refreshAll(), 120);
  });
    })();
